import { useState } from "react";
import { useDispatch } from "react-redux";
import { AppDispatch } from "configureStore";
import { Button, InlineStack, Banner } from "@shopify/polaris";
import { useTranslation } from "react-i18next";
import {
  sendCreateCustomerQuestionnaire,
  postQuestionnaireDryRun,
  cancelCustomerQuestionnaire,
} from "services/customers.service";
import { fetchCustomer } from "containers/customer/customer-container.slice";
import { setDestroy, setIsModalxOpen } from "components/modalx/modalx.slice";
import CustomerQuestionnaireCrossCompanyConfirm from "components/customer-questionnaire-cross-company-confirm/customer-questionnaire-cross-company-confirm";

interface ConfirmModalProps {
  companyId: string;
  customerId: string;
  subject: string;
}

const ConfirmModal: React.FC<ConfirmModalProps> = ({
  companyId,
  customerId,
  subject,
}) => {
  const dispatch: AppDispatch = useDispatch();
  const { t } = useTranslation();
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [crossCompanyPreview, setCrossCompanyPreview] = useState<{
    sourceCompanyName: string;
    attachmentCount: number;
  } | null>(null);

  const closeModal = () => {
    dispatch(setIsModalxOpen(false));
    dispatch(setDestroy());
  };

  const handleCreate = async () => {
    setErrMsg(null);
    try {
      setIsLoading(true);
      if (subject === "generate") {
        const year = new Date().getFullYear();
        if (crossCompanyPreview) {
          return;
        }
        let preview = null;
        try {
          preview = await postQuestionnaireDryRun(companyId, customerId, year);
        } catch (dryRunError) {
          console.error("Error running questionnaire dry-run", dryRunError);
        }
        if (preview?.crossCompany) {
          setCrossCompanyPreview({
            sourceCompanyName: preview.sourceCompanyName || "",
            attachmentCount: preview.attachmentCount || 0,
          });
          return;
        }
        await sendCreateCustomerQuestionnaire(companyId, customerId, year);
      } else if (subject === "cancel") {
        await cancelCustomerQuestionnaire(companyId, customerId);
      }
      dispatch(fetchCustomer({ customerId, companyId: companyId! }));
      setSuccessMessage("ok");
      closeModal();
    } catch (e) {
      console.error("Error creating customer questionnaire", e);
      setErrMsg("error");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="form-warnings">
        {errMsg && (
          <div>
            <Banner tone="critical">{t(errMsg)}</Banner>
          </div>
        )}
        {successMessage && (
          <div>
            <Banner tone="info">{t(successMessage)}</Banner>
          </div>
        )}
      </div>
      {subject === "generate" && !crossCompanyPreview && (
        <h1 style={{ textAlign: "center" }}>
          {t("Are_you_sure_you_want_to_generate_questionnaire")}
        </h1>
      )}
      {subject === "cancel" && (
        <h1 style={{ textAlign: "center" }}>
          {t("Are_you_sure_you_want_to_cancel_questionnaire")}
        </h1>
      )}
      {subject === "generate" && crossCompanyPreview ? (
        <CustomerQuestionnaireCrossCompanyConfirm
          companyId={companyId}
          customerId={customerId}
          year={new Date().getFullYear()}
          sourceCompanyName={crossCompanyPreview.sourceCompanyName}
          attachmentCount={crossCompanyPreview.attachmentCount}
          onSuccess={() => setSuccessMessage("ok")}
        />
      ) : (
        <InlineStack align="space-between">
          <Button onClick={handleCreate} loading={isLoading}>
            {t("Yes")}
          </Button>
          <Button onClick={closeModal}>{t("No")}</Button>
        </InlineStack>
      )}
    </>
  );
};

export default ConfirmModal;
